import axios from 'axios';
import { getCsrfToken, getDevUser, isCsrfRejection, recoverFromCsrfRejection } from './identity';

// Resolution order: runtime config injected by the Docker image at container start
// (window.__APP_CONFIG__, see docker/entrypoint.sh - lets one built image be deployed
// unchanged to every environment) > Vite build-time env var (local `npm run dev`/`vite build`)
// > relative '/api' (same-origin, works behind any reverse proxy with zero configuration).
const API_BASE_URL =
  window.__APP_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || '/api';

export interface SystemConfig {
  port: number;
  database: {
    host: string;
    port: number;
    user: string;
    database: string;
    sslMode: string;
  };
  redis: {
    url: string;
  };
  livekit: {
    url: string;
    apiKey: string;
  };
}

export interface HealthStatus {
  status: string;
  timestamp: string;
}

export interface DatabaseMetrics {
  database_size_bytes: number;
  database_size: string;
  table_count: number;
  estimated_row_count: number;
  connection_count: number;
  active_connections: number;
  postgres_version: string;
  server_started_at: string;
}

export interface Radio {
  id: string;
  org_id: string;
  radio_id: string;
  model: string | null;
  name: string | null;
  status: string; // online, offline, idle, transmitting
  battery_level: number | null;
  last_seen: string | null;
  created_at: string;
  updated_at: string;
}

export interface PresenceData {
  status: string;
  latitude?: number;
  longitude?: number;
  battery?: number;
  last_update: number;
}

export interface AuthUser {
  id: string;
  username: string;
  org_id: string;
  role: string;
}

const TOKEN_KEY = 'ptt_token';
const USER_KEY = 'ptt_user';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const getCurrentUser = (): AuthUser | null => {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
};

export const isAuthenticated = (): boolean => !!getToken();

export const logout = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000,
});

// DEV_MODE act-as header (services/identity.tsx); the server ignores it unless DEV_MODE is on.
apiClient.interceptors.request.use((config) => {
  const devUser = getDevUser();
  if (devUser) config.headers.set('X-Dev-User-ID', devUser);
  // CSRF token of the RadioOS session on state-changing requests (services/identity.ts).
  const method = (config.method || 'get').toUpperCase();
  if (getCsrfToken() && !['GET', 'HEAD', 'OPTIONS'].includes(method)) config.headers.set('X-CSRF-Token', getCsrfToken());
  return config;
});

// A CSRF rejection after the session changed in another tab: the same recovery as the fetch
// wrapper (services/identity.ts); the request is not replayed, the error carries the explanation.
apiClient.interceptors.response.use(undefined, async (error) => {
  const response = error?.response;
  const method = String(error?.config?.method || 'get').toUpperCase();
  if (response && !['GET', 'HEAD', 'OPTIONS'].includes(method) && isCsrfRejection(response.status, String(response.data ?? ''))) {
    const message = await recoverFromCsrfRejection();
    response.data = message;
    error.message = message;
  }
  return Promise.reject(error);
});

apiClient.interceptors.request.use((requestConfig) => {
  const token = getToken();
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

export const login = async (username: string, password: string): Promise<AuthUser> => {
  const response = await apiClient.post<{ token: string; user: AuthUser }>('/auth/login', {
    username,
    password,
  });
  localStorage.setItem(TOKEN_KEY, response.data.token);
  localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
  return response.data.user;
};

// /health is served directly by the backend (not under /api - see infrastructure/docker-compose.yml and
// web/vite.config.ts's separate proxy entry), so this must bypass apiClient's "/api" baseURL -
// using it here previously always 404'd (confirmed live: GET /api/health -> 404, GET /health -> 200).
export const healthCheck = async (signal?: AbortSignal): Promise<HealthStatus> => {
  const response = await fetch('/health', { signal });
  if (!response.ok) throw new Error(`Health check failed: ${response.status}`);
  return response.json();
};

export const listRadios = async (limit = 200, offset = 0): Promise<Radio[]> => {
  const response = await apiClient.get<Radio[]>('/radios', { params: { limit, offset } });
  return response.data ?? [];
};

export const getAllPresence = async (): Promise<Record<string, PresenceData>> => {
  const response = await apiClient.get<Record<string, PresenceData>>('/presence');
  return response.data ?? {};
};

export const getConfig = async (): Promise<SystemConfig> => {
  const response = await apiClient.get<SystemConfig>('/config');
  return response.data;
};

export const updateConfig = async (config: Partial<SystemConfig>): Promise<SystemConfig> => {
  const response = await apiClient.put<SystemConfig>('/config', config);
  return response.data;
};

export const getDatabaseMetrics = async (): Promise<DatabaseMetrics> => {
  const response = await apiClient.get<DatabaseMetrics>('/admin/database/metrics');
  return response.data;
};

// User Management API functions
export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export const getAllUsers = async (): Promise<User[]> => {
  const response = await apiClient.get<User[]>('/admin/users');
  return response.data ?? [];
};

export const getAdmins = async (): Promise<User[]> => {
  const response = await apiClient.get<User[]>('/admin/users/admins');
  return response.data ?? [];
};

export const createUser = async (username: string, email: string, password: string, role: string = 'dispatcher'): Promise<User> => {
  const response = await apiClient.post<User>('/admin/users', {
    username,
    email,
    password,
    role,
  });
  return response.data;
};

export const deleteUser = async (userId: string): Promise<void> => {
  await apiClient.delete(`/admin/users/${userId}`);
};

export const updateUser = async (userId: string, email?: string, role?: string): Promise<User> => {
  const response = await apiClient.put<User>(`/admin/users/${userId}`, {
    email,
    role,
  });
  return response.data;
};

export const changeUserRole = async (userId: string, role: string): Promise<User> => {
  const response = await apiClient.put<User>(`/admin/users/${userId}/role`, {
    role,
  });
  return response.data;
};

// API Keys Management
export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  status: string;
  last_used: string;
  created_at: string;
  key?: string; // Only present on creation
}

export const getAllApiKeys = async (): Promise<ApiKey[]> => {
  const response = await apiClient.get<ApiKey[]>('/settings/api-keys');
  return response.data ?? [];
};

export const createApiKey = async (name: string): Promise<ApiKey> => {
  const response = await apiClient.post<ApiKey>('/settings/api-keys', { name });
  return response.data;
};

export const deleteApiKey = async (keyId: string): Promise<void> => {
  await apiClient.delete(`/settings/api-keys/${keyId}`);
};

export const revokeApiKey = async (keyId: string): Promise<void> => {
  await apiClient.post(`/settings/api-keys/${keyId}/revoke`, {});
};

// System Logs
export interface SystemLog {
  id: string;
  timestamp: string;
  level: string;
  source: string;
  message: string;
}

export const getSystemLogs = async (level: string = 'all', limit: number = 200): Promise<SystemLog[]> => {
  const params: Record<string, string> = { limit: String(limit) };
  if (level && level !== 'all') {
    params.level = level;
  }
  const response = await apiClient.get<SystemLog[]>('/admin/logs', { params });
  return response.data ?? [];
};

export const clearSystemLogs = async (): Promise<void> => {
  await apiClient.delete('/admin/logs');
};

// Backups
export interface BackupInfo {
  name: string;
  size: number;
  size_human: string;
  type: string;
  created_at: string;
}

export const getBackups = async (): Promise<BackupInfo[]> => {
  const response = await apiClient.get<BackupInfo[]>('/admin/backups');
  return response.data ?? [];
};

export const createBackup = async (type: 'full' | 'config' | 'postgres' = 'full'): Promise<BackupInfo> => {
  const response = await apiClient.post<BackupInfo>('/admin/backups', { type });
  return response.data;
};

export const deleteBackup = async (name: string): Promise<void> => {
  await apiClient.delete(`/admin/backups/${encodeURIComponent(name)}`);
};

export const restoreBackup = async (name: string): Promise<void> => {
  await apiClient.post(`/admin/backups/${encodeURIComponent(name)}/restore`);
};

export const getBackupDownloadUrl = (name: string): string => {
  return `${API_BASE_URL}/admin/backups/${encodeURIComponent(name)}/download`;
};

export default apiClient;
